import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, RequireAuth } from "./lib/auth";
import { SiteProvider } from "./lib/site";
import PublicLayout from "./components/site/Layout";
import Home from "./pages/public/Home";
import { Programs, ProgramDetail } from "./pages/public/Programs";
import { Classes, Admissions, Funding, About, Faculty, Success, Faq, Blog, BlogPost, Contact, Legal, NotFound } from "./pages/public/Pages";
import FindProgram from "./pages/public/FindProgram";
import Apply from "./pages/public/Apply";
import Login, { AuthCallback, ResetPassword, VerifyEmail } from "./pages/public/Login";
import StudentPortal from "./student/StudentPortal";
import PortalLayout from "./portal/PortalLayout";
import Dashboard, { Reports, SearchPage } from "./portal/Dashboard";
import ResourceManager from "./portal/ResourceManager";
import { RESOURCES } from "./portal/resources";
import { Crm, LeadDetail } from "./portal/Crm";
import { ApplicationDetail, StudentDetail } from "./portal/Records";
import { MediaLibrary, Settings } from "./portal/Media";
import Notifications from "./portal/Notifications";
import Attendance from "./portal/Attendance";
import PaymentTracking from "./portal/PaymentTracking";
import Certificate from "./pages/Certificate";

const RM_ROUTES = ["programs", "classes", "events", "staff", "faqs", "testimonials", "blog", "announcements", "course", "tasks", "automations", "automation-logs", "email-log", "audit", "users", "students", "applications", "enrollments", "payments", "documents"];

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route element={<PublicLayout />}>
        <Route index element={<Home />} />
        <Route path="programs" element={<Programs />} />
        <Route path="programs/:slug" element={<ProgramDetail />} />
        <Route path="classes" element={<Classes />} />
        <Route path="events" element={<Classes events />} />
        <Route path="admissions" element={<Admissions />} />
        <Route path="funding" element={<Funding />} />
        <Route path="wioa" element={<Navigate to="/funding" replace />} />
        <Route path="about" element={<About />} />
        <Route path="faculty" element={<Faculty />} />
        <Route path="student-success" element={<Success />} />
        <Route path="faq" element={<Faq />} />
        <Route path="blog" element={<Blog />} />
        <Route path="blog/:slug" element={<BlogPost />} />
        <Route path="contact" element={<Contact />} />
        <Route path="find-your-program" element={<FindProgram />} />
        <Route path="apply" element={<Apply />} />
        <Route path="privacy" element={<Legal kind="privacy" />} />
        <Route path="terms" element={<Legal kind="terms" />} />
        <Route path="reset-password" element={<ResetPassword />} />
        <Route path="verify" element={<VerifyEmail />} />
        <Route path="*" element={<NotFound />} />
      </Route>
      <Route path="login" element={<Login />} />
      <Route path="auth/callback" element={<AuthCallback />} />
      <Route path="student/*" element={<RequireAuth><StudentPortal /></RequireAuth>} />
      <Route path="certificate/:id" element={<RequireAuth any><Certificate /></RequireAuth>} />
      <Route path="staff" element={<Navigate to="/admin" replace />} />
      <Route path="admin" element={<RequireAuth staff><PortalLayout /></RequireAuth>}>
        <Route index element={<Dashboard />} />
        <Route path="crm" element={<Crm />} />
        <Route path="crm/:id" element={<LeadDetail />} />
        <Route path="applications/:id" element={<ApplicationDetail />} />
        <Route path="students/:id" element={<StudentDetail />} />
        <Route path="media" element={<MediaLibrary />} />
        <Route path="settings" element={<Settings />} />
        <Route path="reports" element={<Reports />} />
        <Route path="search" element={<SearchPage />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="attendance" element={<Attendance />} />
        <Route path="payment-tracking" element={<PaymentTracking />} />
        {RM_ROUTES.map((r) => <Route key={r} path={r} element={<ResourceManager key={r} cfg={RESOURCES[r]} />} />)}
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SiteProvider>
          <AppRouter />
          <Toaster richColors position="top-right" />
        </SiteProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
